import { ReactNode } from "react";
import classNames from "classnames";
import { ChevronRightSmallIcon } from "@icons";
import { styleWithVars } from "@lib/cssVars";
import styles from "./SpreadsheetGrid.module.scss";

interface TreeToggleProps {
    isExpanded: boolean;
    onToggle: () => void;
    label: string;
}

/** The report and list looks' chevron button, pointing down while expanded. */
export const SpreadsheetTreeToggle = ({
    isExpanded,
    onToggle,
    label,
}: TreeToggleProps) => (
    <button
        className={styles.treeToggle}
        data-expanded={isExpanded || undefined}
        onClick={(event) => {
            event.stopPropagation();
            onToggle();
        }}
        tabIndex={-1}
        aria-label={label}
        aria-expanded={isExpanded}
        type="button"
    >
        <ChevronRightSmallIcon aria-hidden />
    </button>
);

interface Props {
    depth: number;
    indentPx: number;
    isExpanded: boolean;
    /** Set on rows with children; leaves get a spacer of the chevron's width. */
    onToggleExpand?: () => void;
    children: ReactNode;
}

/**
 * Tree column content for the report and list looks: indented by depth, then
 * the row's chevron (or a spacer on leaves, so every label in a group lines
 * up), then the value.
 */
export const SpreadsheetTreeCellContent = ({
    depth,
    indentPx,
    isExpanded,
    onToggleExpand,
    children,
}: Props) => (
    <div
        className={classNames(styles.cellContent, styles.treeCellContent)}
        style={styleWithVars({
            "--spreadsheet-tree-indent": `${depth * indentPx}px`,
        })}
    >
        {onToggleExpand ? (
            <SpreadsheetTreeToggle
                isExpanded={isExpanded}
                onToggle={onToggleExpand}
                label={isExpanded ? "Collapse" : "Expand"}
            />
        ) : (
            <span className={styles.treeToggleSpacer} />
        )}
        {children}
    </div>
);
